import { useMemo } from "react";
import { useACLContext } from "../context";
import type { PermissionAction, ResourceType } from "@shield-acl/core";

/**
 * Estrutura de uma verificação de permissão
 */
export interface PermissionCheck<TContext = any> {
  action: PermissionAction;
  resource?: ResourceType;
  context?: TContext;
}

/**
 * Hook que verifica se o usuário tem QUALQUER uma das permissões listadas (OR)
 *
 * @param permissions - Array de permissões para verificar
 * @returns boolean indicando se tem pelo menos uma das permissões
 *
 * @example
 * ```tsx
 * // Verificar se pode criar OU editar posts
 * const canModifyPosts = useCanAny([
 *   { action: "posts.create" },
 *   { action: "posts.edit" }
 * ])
 *
 * // Com recursos diferentes
 * const canAccessContent = useCanAny([
 *   { action: "posts.read", resource: "posts" },
 *   { action: "pages.read", resource: "pages" },
 *   { action: "admin.access" }
 * ])
 *
 * // Com contexto específico
 * const canInteractWithPost = useCanAny([
 *   { action: "posts.edit", resource: "posts", context: { postId: 123 } },
 *   { action: "posts.moderate", resource: "posts" },
 *   { action: "admin.override" }
 * ])
 * ```
 */
export function useCanAny<TContext = any>(
  permissions: PermissionCheck<TContext>[],
): boolean {
  const { can, user } = useACLContext<TContext>();

  // Memoiza o resultado baseado nas permissões e no usuário
  return useMemo(() => {
    // Se não há permissões para verificar, retorna false
    if (!permissions || permissions.length === 0) {
      return false;
    }

    // Verifica se tem pelo menos uma das permissões
    return permissions.some((permission) =>
      can(permission.action, permission.resource, permission.context),
    );
  }, [can, permissions, user]);
}

/**
 * Versão simplificada do useCanAny que aceita apenas ações como strings
 *
 * @param actions - Array de ações para verificar
 * @returns boolean indicando se tem pelo menos uma das permissões
 *
 * @example
 * ```tsx
 * // Verificação simples de múltiplas ações
 * const canManageUsers = useCanAnyAction([
 *   "users.create",
 *   "users.edit",
 *   "users.delete"
 * ])
 * ```
 */
export function useCanAnyAction(actions: PermissionAction[]): boolean {
  const permissions = useMemo(
    () => actions.map((action) => ({ action })),
    [actions],
  );

  return useCanAny(permissions);
}
